/** Vouchers, loyalty points and quotations (Phase 7). */
import { z } from "zod";
import {
  discountSchema,
  idSchema,
  poishaSchema,
  qtySchema,
  tenderSchema,
  voucherCodeSchema,
} from "./inventory";

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");

export const voucherSchema = z
  .object({
    code: voucherCodeSchema,
    kind: z.enum(["percent", "amount"]),
    /** Basis points for percent, poisha for amount. */
    value: z.number().int().positive(),
    minSpend: poishaSchema.default(0),
    maxDiscount: poishaSchema.nullable().default(null),
    startsAt: z.number().int().positive().nullable().default(null),
    endsAt: z.number().int().positive().nullable().default(null),
    usageLimit: z.number().int().positive().max(1_000_000).nullable().default(null),
    oncePerCustomer: z.boolean().default(false),
    active: z.boolean().default(true),
    note: z.string().trim().max(200).optional(),
  })
  .refine((v) => v.kind !== "percent" || v.value <= 10_000, {
    message: "A percentage can be at most 100%",
    path: ["value"],
  })
  .refine((v) => !v.startsAt || !v.endsAt || v.startsAt < v.endsAt, {
    message: "Ends before it starts",
    path: ["endsAt"],
  });
export type VoucherInput = z.output<typeof voucherSchema>;

export const voucherCheckSchema = z.object({
  code: voucherCodeSchema,
  /** Bill before the voucher, poisha. */
  amount: poishaSchema,
  customerId: idSchema.nullable().optional(),
});

export const pointsAdjustSchema = z.object({
  points: z
    .number()
    .int()
    .refine((n) => n !== 0, "Enter points to add or remove"),
  note: z.string().trim().min(3).max(200),
});
export type PointsAdjust = z.output<typeof pointsAdjustSchema>;

export const quotationSchema = z.object({
  branchId: idSchema,
  customerId: idSchema.nullable().optional(),
  customerName: z.string().trim().max(120).optional(),
  lines: z
    .array(
      z.object({
        variantId: idSchema,
        unitCode: z.string().min(1).max(16),
        qty: qtySchema,
        /** Omit for the customer's list or catalog price. */
        unitPrice: poishaSchema.optional(),
        discount: discountSchema.optional(),
      }),
    )
    .min(1)
    .max(300),
  validUntil: dateOnly,
  note: z.string().trim().max(500).optional(),
});
export type QuotationInput = z.output<typeof quotationSchema>;

export const quotationConvertSchema = z.object({
  registerSessionId: idSchema.optional(),
  payments: z.array(tenderSchema).max(5).default([]),
});
export type QuotationConvert = z.output<typeof quotationConvertSchema>;
